import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { V2SupportItem } from '../api/v2ReportApi';

const LINK_CLASS = 'text-[#026857] underline underline-offset-2 hover:no-underline';

const NEW_TAB = ' (opens in a new tab)';

/**
 * Same-origin app path — `//host` would leave the app, so it does not count, and
 * neither does any backslash: browsers read `\` as `/`, making `/\host` off-site.
 */
const isAppPath = (href: string) =>
  href.startsWith('/') && !href.startsWith('//') && !href.includes('\\');

const isFileUrl = (href: string) => href.startsWith('https://') && !href.includes('\\');

/**
 * A link only when it has somewhere safe to go, otherwise `fallback`.
 *
 * The backend resolves every link, but the href still lands in an anchor, so
 * only an app path or an https URL becomes one. `label` names a link whose
 * visible text is not enough on its own ("Open", "Explainer video").
 */
export function SafeLink({
  href,
  label,
  className = LINK_CLASS,
  fallback = null,
  children,
}: {
  href?: string;
  label?: string;
  className?: string;
  fallback?: ReactNode;
  children: ReactNode;
}) {
  if (href && isAppPath(href)) {
    return (
      <Link to={href} aria-label={label} className={className}>
        {children}
      </Link>
    );
  }
  if (href && isFileUrl(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label && label + NEW_TAB}
        className={className}
      >
        {children}
        {!label && <span className="sr-only">{NEW_TAB}</span>}
      </a>
    );
  }
  return <>{fallback}</>;
}

/** The label, linked when it has somewhere safe to go, then its unlinked note. */
export function SupportItem({ item }: { item: V2SupportItem }) {
  const { label, note, href } = item;
  return (
    <>
      <SafeLink href={href} fallback={label}>
        {label}
      </SafeLink>
      {note && <span className="text-gray-500"> {note}</span>}
    </>
  );
}
